import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Color, Group } from 'three';

import { roundToDecimals } from '../../../../../utils/math/number';
import { isDefined } from '../../../../../utils/variables';
import { Design } from '../../../../../viewer/elements/design';
import { ElementObj } from '../../../../../viewer/elements/element-obj';
import { Geometry } from '../../../../../viewer/elements/geometry';
import { Glb } from '../../../../../viewer/elements/glb';
import { disposeRecursive } from '../../../../../viewer/utilities/three/dispose';
import { useDialog } from '../../../../hooks/use-dialog';
import { useUnitSymbols } from '../../../../hooks/use-unit-symbols';
import { Button } from '../../../button/button';
import { CheckBox } from '../../../check-box/check-box';
import { ColorDialog, ColorIcon, RgbArray } from '../../../color-picker-dialog/color-picker-dialog';
import { Dialog } from '../../../dialog/dialog';
import { Icon } from '../../../icon/icon';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Slider } from '../../../slider/slider';
import { Stack } from '../../../stack/stack';
import { ToggleGroup, ToggleGroupOption } from '../../../toggle-group/toggle-group';

const DECIMAL_PREVIEW_NUMBER = 12.123;
const DEFAULT_DECIMAL_PLACES = 2;
const DEFAULT_FONT_SIZE = 1.4;
const DEFAULT_COLOR: RgbArray = [1, 1, 1];
const DECIMAL_OPTIONS: ToggleGroupOption<number>[] = [
  { value: 0, label: '0' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
];

type Props = {
  asset: ElementObj;
  dialog: ReturnType<typeof useDialog>;
};

type IconProps = {
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
};

export const DisplaySettingsIcon = (props: IconProps) => {
  const { t } = useTranslation();

  return (
    <Icon
      disabled={props.disabled}
      fixedWidth={true}
      icon={['fal', 'cog']}
      mouseDownStyle={{ opacity: 0.5 }}
      stopPropagationOnClick={true}
      title={t('displaySettingsDialog.title', { ns: 'skyviewAssetMenu' })}
      onClick={props.onClick}
      onHoverStyle={{ icon: ['fad', 'cog'] }}
    />
  );
};

export const DisplaySettingsDialog = (props: Props) => {
  const { t } = useTranslation();
  const unitSymbols = useUnitSymbols();

  const colorDialog = useDialog();
  const [showHeightLabels, setShowHeightLabels] = useState<boolean>(
    props.asset.labelsSettings?.showHeightLabels ?? false,
  );
  const [showLengthLabels, setShowLengthLabels] = useState<boolean>(
    props.asset.labelsSettings?.showLengthLabels ?? false,
  );
  const [color, setColor] = useState<RgbArray>(
    props.asset.labelsSettings?.color
      ? (new Color(props.asset.labelsSettings?.color).toArray() as RgbArray)
      : DEFAULT_COLOR,
  );
  const [fontSize, setFontSize] = useState(
    props.asset.labelsSettings?.fontSize ?? DEFAULT_FONT_SIZE,
  );
  const [decimalPlaces, setDecimalPlaces] = useState<number>(
    props.asset.labelsSettings?.decimalPlaces ?? DEFAULT_DECIMAL_PLACES,
  );
  const [sizeAttenuation, setSizeAttenuation] = useState<boolean>(
    props.asset.labelsSettings?.sizeAttenuation ?? true,
  );

  const onConfirm = useCallback(() => {
    if (props.asset.loadingStatus !== 'loaded' || !props.asset.getStateVisibility()) {
      return;
    }

    if (isDefined(props.asset.labelsGroup)) {
      disposeRecursive(props.asset.labelsGroup);
      props.asset.remove(props.asset.labelsGroup);
      props.asset.labelsGroup = undefined;
      props.asset.labelsSettings = undefined;
    }

    if (!showHeightLabels && !showLengthLabels) {
      props.dialog.hide();
      return;
    }

    const labelGroup = new Group();
    props.asset.add(labelGroup);
    props.asset.labelsGroup = labelGroup;
    props.asset.labelsSettings = {
      showHeightLabels,
      showLengthLabels,
      color: new Color(...color),
      fontSize,
      sizeAttenuation: sizeAttenuation,
      decimalPlaces,
    };

    if (
      props.asset instanceof Design ||
      props.asset instanceof Geometry ||
      props.asset instanceof Glb
    ) {
      props.asset.refreshLabelsGroup();
      props.dialog.hide();
      return;
    }

    props.dialog.hide();
  }, [
    props.dialog,
    props.asset,
    color,
    showHeightLabels,
    showLengthLabels,
    fontSize,
    sizeAttenuation,
    decimalPlaces,
  ]);

  const showAdditionalSettings = showHeightLabels || showLengthLabels;

  return (
    <Dialog closeIcon={true} maxWidth={500} onClose={props.dialog.hide}>
      {{
        header: t('options', { ns: 'common' }),
        content: (
          <Stack spacing={1}>
            <CheckBox
              checked={showHeightLabels}
              onChange={() => setShowHeightLabels((old) => !old)}
            >
              {t('displaySettingsDialog.showLabels', { ns: 'skyviewAssetMenu' })}
            </CheckBox>
            <CheckBox
              checked={showLengthLabels}
              onChange={() => setShowLengthLabels((old) => !old)}
            >
              {t('displaySettingsDialog.showDistanceLabels', { ns: 'skyviewAssetMenu' })}
            </CheckBox>
            {showAdditionalSettings && (
              <React.Fragment>
                <LabelledContainer
                  noWrap={true}
                  text={`${t('displaySettingsDialog.decimalPlaces', { ns: 'skyviewAssetMenu' })}: ${roundToDecimals(DECIMAL_PREVIEW_NUMBER, decimalPlaces)} ${unitSymbols.length}`}
                >
                  <ToggleGroup
                    items={DECIMAL_OPTIONS}
                    value={decimalPlaces}
                    onChange={setDecimalPlaces}
                  />
                </LabelledContainer>
                <LabelledContainer
                  noWrap={true}
                  text={`${t('displaySettingsDialog.fontSize', { ns: 'skyviewAssetMenu' })}: ${fontSize}`}
                >
                  <Slider max={5} min={0.05} step={0.05} value={fontSize} onChange={setFontSize} />
                </LabelledContainer>
                <Stack
                  alignItems="baseline"
                  direction="row"
                  justifyContent="flex-start"
                  spacing={0.5}
                >
                  <ColorIcon
                    data-color={color}
                    title={t('section.attributes.attributeColorTypeSection.changeColorIcon', {
                      ns: 'skyviewDesign',
                    })}
                    onClick={() => {
                      colorDialog.show();
                    }}
                  />
                  <label style={{ flex: 1, fontSize: '0.95em' }}>
                    {t('colorPickerDialog.color', { ns: 'components' })}
                  </label>
                </Stack>

                <CheckBox
                  checked={sizeAttenuation}
                  onChange={() => setSizeAttenuation((old) => !old)}
                >
                  {t('displaySettingsDialog.sizeAttenuation', { ns: 'skyviewAssetMenu' })}
                </CheckBox>
              </React.Fragment>
            )}
            {colorDialog.render(
              <ColorDialog dialog={colorDialog} value={color} onConfirm={setColor} />,
            )}
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button color="primary" type="button" variant="contained" onClick={onConfirm}>
                {t('confirm', { ns: 'common' })}
              </Button>
              <Button type="button" variant="text" onClick={props.dialog.hide}>
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};
